import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ShieldCheck, Loader2, AlertTriangle, ToggleLeft, ToggleRight } from 'lucide-react';
import { SecurityCoverageHeatmap } from '@techstack/ui';
import { useTimeFilterContext } from '../../contexts/TimeFilterContext';
import { useListsContext } from '../../contexts/ListsContext';
import { loggedGet } from './loggedFetch';
import logger from '../../lib/logger';
import './SecurityCoverageMatrix.css';

/**
 * SecurityCoverageMatrix — Security coverage heatmap by product category or ZT pillar.
 *
 * Two tabs:
 *   - Tab A (default): Product Category Matrix (NGFW, SIEM, etc.)
 *   - Tab B: Zero Trust Pillar Matrix (IDENTITY, DEVICE, etc.)
 *
 * This is a standalone component that can be embedded in VCA or used as its own page.
 */

const QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 30 * 60 * 1000, // 30 minutes
  refetchOnWindowFocus: false,
};

function SecurityCoverageMatrix({ onDrilldown, departmentIds, vendor }) {
  const { years, dateRange } = useTimeFilterContext();
  const { selectedList } = useListsContext();
  const [activeTab, setActiveTab] = useState('category'); // 'category' | 'pillar'
  const [colorMode, setColorMode] = useState('log');

  // Build query params
  const deptKey = departmentIds?.length ? departmentIds.join(',') : null;

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (deptKey) params.set('department_ids', deptKey);
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    if (vendor) params.set('vendor', vendor);
    return params.toString();
  }, [deptKey, dateRange, vendor]);

  // Fetch category data
  const categoryPath = `/api/vendor-analysis/security-coverage/by-category${queryParams ? `?${queryParams}` : ''}`;
  const {
    data: categoryData,
    isLoading: categoryLoading,
    error: categoryError,
  } = useQuery({
    queryKey: ['security-coverage', 'category', deptKey, dateRange?.startDate, dateRange?.endDate, vendor],
    queryFn: loggedGet('SecurityCoverageMatrix:fetch-category', categoryPath),
    ...QUERY_OPTIONS,
    enabled: activeTab === 'category',
  });

  // Fetch pillar data
  const pillarPath = `/api/vendor-analysis/security-coverage/by-pillar${queryParams ? `?${queryParams}` : ''}`;
  const {
    data: pillarData,
    isLoading: pillarLoading,
    error: pillarError,
  } = useQuery({
    queryKey: ['security-coverage', 'pillar', deptKey, dateRange?.startDate, dateRange?.endDate, vendor],
    queryFn: loggedGet('SecurityCoverageMatrix:fetch-pillar', pillarPath),
    ...QUERY_OPTIONS,
    enabled: activeTab === 'pillar',
  });

  const data = activeTab === 'category' ? categoryData : pillarData;
  const isLoading = activeTab === 'category' ? categoryLoading : pillarLoading;
  const error = activeTab === 'category' ? categoryError : pillarError;

  // Handle cell click for drill-down
  const handleCellClick = useCallback((cell, row, col) => {
    logger.vendorAnalysis.info('SecurityCoverageMatrix:drilldown', {
      tab: activeTab,
      department: row.label,
      departmentId: row.id,
      column: col.id,
      spend: cell.spend,
      mappingTier: cell.mappingTier,
    });

    if (onDrilldown) {
      onDrilldown({
        type: 'security-coverage',
        tab: activeTab,
        department: row.label,
        departmentId: row.id,
        [activeTab === 'category' ? 'productCategory' : 'ztPillar']: col.id,
        spend: cell.spend,
        mappingTier: cell.mappingTier,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
    }
  }, [activeTab, onDrilldown, dateRange]);

  return (
    <motion.div
      className="security-coverage-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <div className="security-coverage-header">
        <div className="security-coverage-title">
          <ShieldCheck size={20} />
          <div>
            <h2>Security Coverage Matrix</h2>
            <p>
              Agency × {activeTab === 'category' ? 'product category' : 'Zero Trust pillar'} spend intensity.
              {data?.summary?.reconciles && (
                <span className="security-coverage-reconcile-ok"> Cells reconcile to totals.</span>
              )}
            </p>
          </div>
        </div>

        <div className="security-coverage-controls">
          {/* Tab switcher */}
          <div className="security-coverage-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'category'}
              className={`security-coverage-tab ${activeTab === 'category' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('category')}
            >
              Product Categories
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'pillar'}
              className={`security-coverage-tab ${activeTab === 'pillar' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('pillar')}
            >
              ZT Pillars
            </button>
          </div>

          {/* Color mode toggle */}
          <button
            className="security-coverage-mode-btn"
            onClick={() => setColorMode(m => m === 'log' ? 'linear' : 'log')}
            title={colorMode === 'log' ? 'Switch to linear scale' : 'Switch to log scale'}
          >
            {colorMode === 'log' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            <span>{colorMode === 'log' ? 'Log' : 'Linear'}</span>
          </button>
        </div>
      </div>

      {/* Scope indicator */}
      {departmentIds?.length > 0 && selectedList && (
        <div className="security-coverage-scope">
          Scoped to <strong>{selectedList.name}</strong> ({departmentIds.length} agencies)
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="security-coverage-loading">
          <Loader2 size={16} className="security-coverage-spin" />
          Loading security coverage data...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="security-coverage-error">
          <AlertTriangle size={16} />
          Failed to load security coverage: {error.message}
        </div>
      )}

      {/* Heatmap */}
      {!isLoading && !error && data && (
        <SecurityCoverageHeatmap
          rows={data.rows}
          columns={data.columns}
          cells={data.cells}
          maxCellSpend={data.maxCellSpend}
          summary={data.summary}
          maxHeight={600}
          colorMode={colorMode}
          showValues={true}
          onCellClick={handleCellClick}
        />
      )}

      {/* Empty state */}
      {!isLoading && !error && (!data || data.rows?.length === 0) && (
        <div className="security-coverage-empty">
          <ShieldCheck size={24} />
          <p>No security coverage data available for the selected filters.</p>
        </div>
      )}
    </motion.div>
  );
}

export default SecurityCoverageMatrix;
